// What a polygon or a wall numbers by its vertices and sides — a polygon's side
// locks, pinned lengths and relations, a wall's pinned segments and its doors /
// panes / openings — and how all of it follows when the ring changes: a vertex put
// in or taken out, a corner bevelled, a line split, exploded or joined
// (docs/claude/dimensions.md, *Anchors*). One rule, read from where each new side
// came from: a side kept whole keeps everything, a piece of a side keeps its lock
// but not its length, sides merged keep a lock they all agreed on, a new side keeps
// nothing. DOM-free; anchors.ts runs it for the drawing's shapes, vertexEdit for a
// part's own shapes and a room's outline.
import type {
  ShapeRelation,
  Vec2,
  WallFeature,
} from '../shared/types';
import type { VertexRemap } from './polyRoom';
import { reprojectWallFeatures } from './wallFeatures';

/** A ring's change: its vertices before and after, and where each old vertex went
 *  on it (null: gone, or onto another object). */
export interface RingChange {
  before: readonly Vec2[];
  after: readonly Vec2[];
  /** A polygon's ring (its last vertex joins its first); a wall's is open. */
  closed: boolean;
  map: VertexRemap;
}

/** Where one new side came from: the old sides it runs on (`from`, in order),
 *  whether it is one of them whole (from its first vertex to its second), and how
 *  far into its first old side it starts (0: at that side's first vertex). */
export interface SideSource { from: number[]; whole: boolean; start: number }

const EPS = 1e-6;
/** How near (mm) a new vertex must be to an old side to lie on it. */
const ON = 1e-3;

/** Where along the old ring each new vertex sits, counted in sides: old vertex k
 *  at k, a point a fraction t along side e at e + t; null when it is off it. */
function stations(c: RingChange): Array<number | null> {
  const n0 = c.before.length, n1 = c.after.length;
  const s: Array<number | null> = new Array(n1).fill(null);
  for (let v = 0; v < n0; v++) {
    const k = c.map(v);
    if (k != null && k >= 0 && k < n1 && s[k] == null) s[k] = v;
  }
  const sides = c.closed ? n0 : n0 - 1;
  const fwd = (at: number, prev: number | null): number => {
    if (prev == null) return at;
    if (c.closed) return (((at - prev) % n0) + n0) % n0;
    return at - prev >= -EPS ? at - prev : n0 + prev - at;
  };
  for (let j = 0; j < n1; j++) {
    if (s[j] != null || n0 < 2) continue;
    const p = c.after[j];
    const near: Array<{ at: number; d: number }> = [];
    for (let e = 0; e < sides; e++) {
      const a = c.before[e], b = c.before[(e + 1) % n0];
      const dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy;
      const t = L2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2)) : 0;
      const d = Math.hypot(a.x + dx * t - p.x, a.y + dy * t - p.y);
      if (d <= ON) near.push({ at: c.closed ? (e + t) % n0 : e + t, d });
    }
    if (!near.length) continue;
    // The nearest; of places as near (an open ring's two ends at one point), the
    // one that comes next after the vertex before it.
    let prev: number | null = null;
    for (let k = 1; k < n1 && prev == null; k++) {
      if (!c.closed && j - k < 0) break;
      prev = s[(j - k + n1) % n1];
    }
    const best = Math.min(...near.map((x) => x.d));
    s[j] = near.filter((x) => x.d <= best + EPS).sort((x, y) => fwd(x.at, prev) - fwd(y.at, prev))[0].at;
  }
  // A vertex put in off the old ring between two old neighbours is still put into
  // the side they ran on.
  for (let j = 0; j < n1; j++) {
    if (s[j] != null) continue;
    if (!c.closed && (j === 0 || j === n1 - 1)) continue;
    const a = s[(j - 1 + n1) % n1], b = s[(j + 1) % n1];
    if (a == null || b == null || !isInt(a) || !isInt(b)) continue;
    if (c.closed ? (a + 1) % n0 === b : a + 1 === b) s[j] = a + 0.5;
  }
  return s;
}

const isInt = (x: number): boolean => Math.abs(x - Math.round(x)) < EPS;

/** Where each new side of the ring came from (`SideSource`), null for a new side
 *  — a bevel across a corner, a ring closed anew. A side inside one old side is a
 *  piece of it (whole when it runs vertex to vertex); a side that runs, vertex to
 *  vertex, across old vertices that went away is those sides merged. A wall's side
 *  may run backwards (a joined line). */
export function sideSources(c: RingChange): Array<SideSource | null> {
  const n0 = c.before.length, n1 = c.after.length;
  const s = stations(c);
  const wrap = (e: number): number => (c.closed ? e % n0 : e);
  const between = (a: number, b: number): SideSource | null => {
    let lo = a, span = b - a;
    if (c.closed) span = ((span % n0) + n0) % n0;
    else if (span < 0) { lo = b; span = -span; }
    if (span < EPS) return null;
    const first = Math.floor(lo + EPS), last = Math.ceil(lo + span - EPS) - 1;
    const from: number[] = [];
    for (let e = first; e <= last; e++) from.push(wrap(e));
    // Across an old vertex still on this ring, it is no side made from these.
    for (let e = first + 1; e <= last; e++) if (c.map(wrap(e)) != null) return null;
    const start = Math.max(0, lo - first);
    if (from.length === 1) return { from, whole: isInt(lo) && Math.abs(span - 1) < EPS, start };
    return isInt(lo) && isInt(lo + span) ? { from, whole: false, start } : null;
  };
  const out: Array<SideSource | null> = [];
  const sides = c.closed ? n1 : n1 - 1;
  for (let j = 0; j < sides; j++) {
    const a = s[j], b = s[(j + 1) % n1];
    out.push(a == null || b == null ? null : between(a, b));
  }
  return out;
}

/** What a polygon or a wall numbers by its ring's vertices and sides. */
export interface RingFields {
  /** A polygon's side locks (horizontal / vertical), one per side. */
  edgeConstraints?: Array<'h' | 'v' | null>;
  /** A polygon's pinned side lengths, one per side. */
  edgeLengths?: Array<number | null>;
  /** A polygon's relations between its sides and vertices. */
  relations?: ShapeRelation[];
  /** A wall's / line's pinned segment lengths, one per segment. */
  segLengths?: Array<number | null>;
  /** A drawn wall's doors, panes and openings, each on its segment. */
  features?: WallFeature[];
}
type Field = keyof RingFields;

/** One object's part in a ring's new fields: its fields and vertices as they were,
 *  where its vertices went on this ring, and whether all of them stayed on it
 *  (`alone`: its features may go to any side; else only to one they lie on). */
export interface RingSource {
  points: readonly Vec2[];
  fields: RingFields;
  map: VertexRemap;
  alone: boolean;
}

/** A source with its sides worked out against the ring. */
interface Sided extends RingSource { sides: Array<SideSource | null> }

/** One value per new side, from the first source that side came from. */
function perSide<T>(srcs: Sided[], n: number, take: (s: Sided, side: SideSource) => T | null | undefined): Array<T | null> | undefined {
  const out: Array<T | null> = new Array(n).fill(null);
  for (let j = 0; j < n; j++) {
    for (const s of srcs) {
      const side = s.sides[j];
      if (!side) continue;
      out[j] = take(s, side) ?? null;
      break;
    }
  }
  return out.some((v) => v != null) ? out : undefined;
}

/** Remap a relation's sides and vertices, dropping it when one is gone or the
 *  pair collapses to one. */
function remapRelation(rel: ShapeRelation, side: (i: number) => number | null, vert: (i: number) => number | null): ShapeRelation | null {
  if ('edges' in rel) {
    const a = side(rel.edges[0]), b = side(rel.edges[1]);
    return a == null || b == null || a === b ? null : { ...rel, edges: [a, b] };
  }
  if ('verts' in rel) {
    const a = vert(rel.verts[0]), b = vert(rel.verts[1]);
    return a == null || b == null || a === b ? null : { ...rel, verts: [a, b] };
  }
  const v = vert(rel.vert);
  return v == null ? null : { ...rel, vert: v };
}

/** The new side an old side's relations go to: the side kept whole, else its
 *  first piece, else the merged side it begins. */
function sideFor(sides: Array<SideSource | null>): (e: number) => number | null {
  return (e) => {
    let best: number | null = null, key = Infinity;
    sides.forEach((s, j) => {
      if (!s || s.from[0] !== e) return;
      const k = s.whole ? -1 : s.from.length > 1 ? 2 + s.start : s.start;
      if (k < key) { key = k; best = j; }
    });
    return best;
  };
}

/** How each field follows the ring — one entry per field, so a field added to
 *  `RingFields` does not compile until it has its rule. */
const FOLLOW: { [K in Field]: (srcs: Sided[], into: readonly Vec2[], sides: number) => RingFields[K] } = {
  // A lock holds on every piece of its side, and on sides merged that all had it.
  edgeConstraints: (srcs, _, n) => perSide(srcs, n, (s, side) => {
    const c = s.fields.edgeConstraints;
    const v = c?.[side.from[0]] ?? null;
    return v && side.from.every((e) => c?.[e] === v) ? v : null;
  }),
  // A length holds only on its side kept whole.
  edgeLengths: (srcs, _, n) => perSide(srcs, n, (s, side) => (side.whole ? s.fields.edgeLengths?.[side.from[0]] : null)),
  segLengths: (srcs, _, n) => perSide(srcs, n, (s, side) => (side.whole ? s.fields.segLengths?.[side.from[0]] : null)),
  relations: (srcs) => {
    const out = srcs.flatMap((s) => (s.fields.relations ?? [])
      .map((r) => remapRelation(r, sideFor(s.sides), s.map))
      .filter((r): r is ShapeRelation => r != null));
    return out.length ? out : undefined;
  },
  // A door / pane / opening goes to the side it now lies on.
  features: (srcs, into) => {
    const out = srcs.flatMap((s) => (s.fields.features?.length
      ? reprojectWallFeatures([...s.points], [...into], s.fields.features, s.alone ? Infinity : 1)
      : []));
    return out.length ? out : undefined;
  },
};
const FIELDS = Object.keys(FOLLOW) as Field[];

/** A copy of what `obj` numbers by its ring. */
export function ringFields(obj: RingFields): RingFields {
  const out: RingFields = {};
  for (const k of FIELDS) if (obj[k] !== undefined) (out as Record<Field, unknown>)[k] = structuredClone(obj[k]);
  return out;
}

/** A ring's fields after its change, from every object its vertices came from
 *  (`into`: its vertices now). Only the fields some source had are given; an
 *  undefined one is to be removed. */
export function renumberRing(sources: readonly RingSource[], into: readonly Vec2[], closed: boolean): RingFields {
  const sided: Sided[] = sources.map((s) => ({ ...s, sides: sideSources({ before: s.points, after: into, closed, map: s.map }) }));
  const n = closed ? into.length : Math.max(0, into.length - 1);
  const out: RingFields = {};
  for (const k of FIELDS) {
    if (!sources.some((s) => s.fields[k] !== undefined)) continue;
    (out as Record<Field, unknown>)[k] = FOLLOW[k](sided, into, n);
  }
  return out;
}

/** Write a ring's new fields onto its object (an undefined one is removed). */
export function writeRingFields(obj: RingFields, fields: RingFields): void {
  for (const k of Object.keys(fields) as Field[]) {
    if (fields[k] === undefined) delete obj[k];
    else (obj as Record<Field, unknown>)[k] = fields[k];
  }
}

/**
 * Change one ring's vertices and keep what its object numbers by them: `change`
 * changes them and returns how they were renumbered (null: they were not). For a
 * shape outside the drawing — a part's own polygon or line, a room's outline seen
 * as a polygon; the drawing's own follow through `moveVertices` (anchors.ts).
 */
export function followRing(obj: RingFields & { points: Vec2[] }, closed: boolean, change: () => VertexRemap | null): void {
  const fields = ringFields(obj);
  const before = obj.points.map((p) => ({ x: p.x, y: p.y }));
  const map = change();
  if (!map || !Object.keys(fields).length) return;
  writeRingFields(obj, renumberRing([{ points: before, fields, map, alone: true }], obj.points, closed));
}
